<template>
  <v-container>
    <div class="base-page">
      <logo-with-title :title="$t('payment.waitingPage.title')" />

      <v-row align="center" justify="center" class="mt-5">
        <v-col class="text-center">
          <v-progress-circular
            :size="100"
            :width="10"
            color="primary"
            indeterminate
          />
          <h2 class="primary--text mt-6">
            {{ $t("payment.waitingPage.checking") }}
          </h2>
          <h3 class="text-center mt-3">
            {{ $t("payment.waitingPage.description") }}
          </h3>
          <h3 class="text-center mt-2">
            {{ $t("payment.waitingPage.timeLeft", { time: timeLeft }) }}
          </h3>
        </v-col>
      </v-row>

      <v-row>
        <lang-select />
      </v-row>
    </div>
  </v-container>
</template>

<script>
import axios from "@/packages/MyAxios";
import LogoWithTitle from "@/components/LogoWithTitle";
import LangSelect from "@/components/LangSelect";

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 180000;

export default {
  name: "PaymentWaitingPage",
  components: { LogoWithTitle, LangSelect },
  data() {
    return {
      pollIntervalId: null,
      countdownIntervalId: null,
      timeoutId: null,
      deadlineAt: Date.now() + TIMEOUT_MS,
    };
  },
  computed: {
    token() {
      return this.$route.query.token || "";
    },
    secondsLeft() {
      const ms = Math.max(this.deadlineAt - Date.now(), 0);
      return Math.ceil(ms / 1000);
    },
    timeLeft() {
      const minutes = String(Math.floor(this.secondsLeft / 60)).padStart(
        2,
        "0"
      );
      const seconds = String(this.secondsLeft % 60).padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
  },
  async created() {
    if (!this.token) {
      this.goToError(this.$t("payment.waitingPage.missingToken"));
      return;
    }

    await this.checkSubscription();
    this.startPolling();
    this.startCountdown();
    this.startTimeout();
  },
  beforeDestroy() {
    this.stopTimers();
  },
  methods: {
    startPolling() {
      this.pollIntervalId = setInterval(() => {
        this.checkSubscription();
      }, POLL_INTERVAL_MS);
    },
    startCountdown() {
      this.countdownIntervalId = setInterval(() => {
        this.$forceUpdate();
      }, 1000);
    },
    startTimeout() {
      this.timeoutId = setTimeout(() => {
        this.goToError(this.$t("payment.waitingPage.timeout"));
      }, TIMEOUT_MS);
    },
    stopTimers() {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
      }
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId);
        this.countdownIntervalId = null;
      }
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    },
    async checkSubscription() {
      try {
        const response = await axios.get("purchase/subscription-status", {
          params: { token: this.token },
        });

        if (
          response.data.status === "ready" &&
          response.data.subscriptionToken
        ) {
          this.stopTimers();
          this.$router.replace({
            name: "thank-you-page",
            query: { token: response.data.subscriptionToken },
          });
          return;
        }

        if (response.data.status === "not_found") {
          this.goToError(this.$t("payment.waitingPage.purchaseNotFound"));
        }
      } catch (error) {
        // Keep polling until timeout for transient failures.
        console.error("Failed checking subscription status", error);
      }
    },
    goToError(message) {
      this.stopTimers();
      this.$router.replace({
        name: "payment-error-page",
        query: { message },
      });
    },
  },
};
</script>

<style scoped>
</style>
